import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './Orders.css';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const { t, language, isRTL } = useLanguage();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await fetch('/api/orders/my-orders', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setOrders(data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: 'warning',
      processing: 'primary',
      shipped: 'primary',
      delivered: 'success',
      cancelled: 'danger'
    };
    return statusMap[status] || 'secondary';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return `${price.toFixed(2)} ${t('currency')}`;
  };

  const getStatusText = (status) => {
    return t(status) || status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getPaymentInfo = (order) => {
    const method = order.paymentMethod || 'cod';
    const status = order.paymentStatus || 'pending';

    if (method === 'card') {
      let statusLabel = isRTL ? 'قيد الانتظار' : 'Pending';
      let className = 'payment-badge-pending';

      if (status === 'paid') {
        statusLabel = isRTL ? 'مدفوع' : 'Paid';
        className = 'payment-badge-success';
      } else if (status === 'refunded') {
        statusLabel = isRTL ? 'مسترد' : 'Refunded';
        className = 'payment-badge-refunded';
      } else if (status === 'partially_refunded') {
        statusLabel = isRTL ? 'مسترد جزئياً' : 'Partial Refund';
        className = 'payment-badge-refunded';
      } else if (status === 'failed') {
        statusLabel = isRTL ? 'فشل' : 'Failed';
        className = 'payment-badge-failed';
      }

      return {
        icon: '💳',
        label: isRTL ? 'بطاقة' : 'Card',
        status: statusLabel,
        className: className
      };
    } else {
      // COD is pending until delivered
      const isPaid = order.status === 'delivered';
      return {
        icon: '💵',
        label: isRTL ? 'عند الاستلام' : 'COD',
        status: isPaid ? (isRTL ? 'تم الدفع' : 'Collected') : (isRTL ? 'عند التسليم' : 'On Delivery'),
        isPaid: isPaid,
        className: isPaid ? 'payment-badge-success' : 'payment-badge-cod'
      };
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
      <div className={`orders-page ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container">
        <div className="page-header">
          <h1>{t('myOrders')}</h1>
          <p>{t('viewTrackOrders')}</p>
        </div>

        {orders.length === 0 ? (
          <div className="empty-orders">
            <span className="empty-icon">📦</span>
            <h2>{t('noOrders')}</h2>
            <p>{t('noOrdersDesc')}</p>
            <Link to="/products" className="btn btn-primary btn-lg">
              {t('startShopping')}
            </Link>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map(order => (
              <Link to={`/orders/${order.id}`} key={order.id} className="order-card">
                <div className="order-header">
                  <div>
                    <span className="order-id">{t('orders')} #{order.id}</span>
                    <span className="order-date">{formatDate(order.createdAt)}</span>
                  </div>
                  <div className="order-badges">
                    {(() => {
                      const payment = getPaymentInfo(order);
                      return (
                        <span className={`badge ${payment.className}`}>
                          {payment.icon} {payment.label} • {payment.status}
                        </span>
                      );
                    })()}
                    <span className={`badge badge-${getStatusBadge(order.status)}`}>
                      {getStatusText(order.status)}
                    </span>
                  </div>
                </div>

                <div className="order-items">
                  {order.items.slice(0, 3).map((item, index) => (
                    <div key={index} className="order-item-thumb">
                      <img src={item.image} alt={item.name} />
                    </div>
                  ))}
                  {order.items.length > 3 && (
                    <div className="order-item-more">
                      +{order.items.length - 3}
                    </div>
                  )}
                </div>

                <div className="order-footer">
                  <span className="order-total">{formatPrice(order.total)}</span>
                  <span className="view-details">{t('viewDetails')} {isRTL ? '←' : '→'}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
        </div>
      </div>
  );
};

export default Orders;
